// libraries
import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';

import { Button, Form, Input, notification, FormInstance, Typography } from 'antd';
import { CheckOutlined, DeleteFilled, ArrowRightOutlined } from '@ant-design/icons';

// components
import { TextEditor } from '../../../shared/text-editor';
import { Spinner } from '../../../shared/spinner';
import { DeleteModal } from '../../../shared/delete-modal';

// services
import * as SubjectService from '../../../../services/bb-api/subject';
import { txt } from '../../../../locales/i18n';

const fallbackImageSource: string =
	'https://www.pngkey.com/png/detail/233-2332677_image-500580-placeholder-transparent.png';

export function SubjectPageRoute(): JSX.Element {
	const [loading, setLoading] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [subject, setSubject] = useState<Record<string, any>>({});
	const [imageSource, setImageSource] = useState<string>('');
	const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());

	const formRef = useRef<FormInstance>(null);
	const history = useHistory();
	const { id } = useParams<any>();

	useEffect(() => {
		async function getSubject(): Promise<void> {
			setLoading(true);

			try {
				const subject: Record<string, any> = await SubjectService.get(id);
				setSubject(subject);
				setImageSource(subject.image);
				const contentBlock = htmlToDraft(subject.html);

				if (contentBlock) {
					const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
					const editorState = EditorState.createWithContent(contentState);
					setEditorState(editorState);
				}
			} catch (error) {
				const message: string = (error as Error).name;
				const description: string = (error as Error).message;
				notification.error({ message, description });
			}

			setLoading(false);
		}

		getSubject();
	}, [id]);

	async function handleUpdateSubject(values: any): Promise<void> {
		const form = formRef.current;
		if (form === null) return;

		const [titleError]: string[] = form.getFieldError('title');
		if (titleError !== undefined) {
			notification.error({ message: titleError });
			return;
		}

		const [imageError]: string[] = form.getFieldError('image');
		if (imageError !== undefined) {
			notification.error({ message: imageError });
			return;
		}

		const editorContent: ContentState = editorState.getCurrentContent();
		const rawDraft = convertToRaw(editorContent);
		const html: string = draftToHtml(rawDraft);
		values.html = html;

		if (html.trim().length < 20) {
			notification.error({ message: txt('subject.content too short') });
			return;
		}

		setLoading(true);

		try {
			await SubjectService.update(id, values);
			notification.success({ message: txt('notification.update successful') });
			history.push('/subjects');
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setLoading(false);
	}

	async function handleFormValueChange(changedValues: any): Promise<void> {
		const form = formRef.current;
		if (form === null) return;

		if (changedValues.hasOwnProperty('title')) {
			const title: string = changedValues.title;

			let subjectId: string = title.trim().toLowerCase();
			subjectId = subjectId.replace(/[\sæøå]/gi, (match) => {
				if (match === 'æ') return 'ae';
				if (match === 'ø') return 'oe';
				if (match === 'å') return 'aa';
				return '-';
			});

			form.setFields([{ name: 'id', value: subjectId }]);
			form.setFields([{ name: 'title', errors: [] }]);
			if (id === subjectId) return;

			try {
				await SubjectService.validateId(subjectId);
			} catch (error) {
				form.setFields([{ name: 'title', errors: [txt('subject.already exists')] }]);
			}
		}

		if (changedValues.hasOwnProperty('image')) {
			const imageSource: string = changedValues.image;
			setImageSource(imageSource);
		}
	}

	function handlerImageLoad(event: any): void {
		const form = formRef.current;
		if (form === null) return;

		if (event.target.src === fallbackImageSource) {
			form.setFields([{ name: 'image', errors: [txt('subject.cant find image')] }]);
		} else {
			form.setFields([{ name: 'image', errors: [] }]);
		}
	}

	function handleFallbackImage(event: any): void {
		if (event.target.src === fallbackImageSource) return;
		event.target.src = fallbackImageSource;
	}

	async function handleDelete(): Promise<void> {
		await SubjectService.remove(id);
		history.replace('/subjects');
	}

	if (loading) return <Spinner />;

	return (
		<StyledMain>
			<Form
				initialValues={subject}
				ref={formRef}
				onFinish={handleUpdateSubject}
				onValuesChange={handleFormValueChange}
				labelCol={{ span: 2 }}
			>
				<Form.Item label={txt('global.title')} name="title">
					<Input required placeholder={txt('global.title')} />
				</Form.Item>

				<Form.Item label="ID" name="id">
					<Input placeholder="" disabled />
				</Form.Item>

				<Form.Item label="Meta" name="metaDescription">
					<Input.TextArea required placeholder="Meta description" showCount maxLength={160} />
				</Form.Item>

				<Form.Item label={txt('global.image')} name="image">
					<Input required placeholder={txt('global.image')} />
				</Form.Item>

				<Form.Item label=" " colon={false}>
					<img
						className="image"
						src={imageSource}
						alt="subject profile"
						onLoad={handlerImageLoad}
						onError={handleFallbackImage}
					/>
				</Form.Item>

				<Form.Item label={txt('global.content')}>
					<TextEditor editorState={editorState} setEditorState={setEditorState} />
				</Form.Item>

				<Form.Item label=" " colon={false}>
					<div className="submit-container">
						<Button type="primary" htmlType="submit" loading={loading}>
							{txt('global.update')} <CheckOutlined />
						</Button>

						<Button
							type="link"
							href={`https://boblberg.dk/emner/${subject.id}`}
							target="_blank"
							rel="noreferrer"
						>
							{txt('global.open')} <ArrowRightOutlined />
						</Button>

						<Button onClick={history.goBack} className="cancel-button">
							{txt('global.cancel')}
						</Button>

						<Button onClick={() => setShowDeleteModal(true)} danger>
							{txt('global.delete')} <DeleteFilled />
						</Button>
					</div>
				</Form.Item>
			</Form>

			<DeleteModal useVisible={[showDeleteModal, setShowDeleteModal]} onConfirm={handleDelete} />
		</StyledMain>
	);
}

const StyledMain = styled.main`
	padding: 2rem;

	form {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.image {
		width: 100%;
		max-width: 350px;
	}

	.rdw-link-modal {
		height: fit-content;
		width: fit-content;
	}

	.editor-content-container {
		height: 24rem;
		overflow-y: auto;
		border: 1px solid grey;
		padding-inline: 1rem;
	}

	.submit-container {
		width: 100%;
		display: flex;
		gap: 1rem;

		.cancel-button {
			margin-left: auto;
		}
	}
`;
